import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { GetglobalsettingsService } from './getglobalsettings.service';
import { HiddenDocsLogin, HiddenDocsQuery, HiddenDocsResponse, UnlistedDocsQuery, UnlistedDocsResponse } from '../shared/admin-hidden-documents';

/**
 * REST client for the admin "hidden documents" inspector
 * (`/AdminHiddenDocuments/*`, `realm:portal-admin` server-side, read-only).
 * Does not gate access itself — the host page does, and the API's 403 is the
 * real boundary. No retry: a 400/403 is deterministic, and the callers
 * surface the API's `error` text in a toast.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminHiddenDocumentsApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + '/AdminHiddenDocuments' + url;
  }

  /** DB-context headers (mandatory) via `getConnectionOptions()`, plus the main company the API scopes by. */
  private _options(params?: HttpParams) {
    return {
      headers: {
        ...this.globalsettingsservice.getConnectionOptions(),
        'Webyto-Main-Company': this.globalsettingsservice.getMainCompanyId().toString(),
      },
      params,
    };
  }

  /** `GET /AdminHiddenDocuments/logins` — one row per login. */
  getHiddenDocsLogins(): Observable<HiddenDocsLogin[]> {
    return this.http.get<HiddenDocsLogin[]>(this._getApiUrl('/logins'), this._options());
  }

  /** `GET /AdminHiddenDocuments/documents` — documents the login (or the what-if ladder, which wins) would not see. */
  getHiddenDocs(q: HiddenDocsQuery): Observable<HiddenDocsResponse> {
    let params = new HttpParams()
      .set('fromDate', q.fromDate)
      .set('_start', String(q.start))
      .set('_fetch', String(q.fetch));
    if (q.login) { params = params.set('login', q.login); }
    if (q.ladder !== null && q.ladder !== undefined) { params = params.set('ladder', String(q.ladder)); }
    if (q.toDate) { params = params.set('toDate', q.toDate); }
    if (q.docTypes && q.docTypes.length) { params = params.set('docTypes', q.docTypes.join(',')); }
    return this.http.get<HiddenDocsResponse>(this._getApiUrl('/documents'), this._options(params));
  }

  /** `GET /AdminHiddenDocuments/unlisted` — documents that appear in no list for anyone. Independent of any login/ladder. */
  getUnlistedDocs(q: UnlistedDocsQuery): Observable<UnlistedDocsResponse> {
    let params = new HttpParams()
      .set('fromDate', q.fromDate)
      .set('_start', String(q.start))
      .set('_fetch', String(q.fetch));
    if (q.toDate) { params = params.set('toDate', q.toDate); }
    if (q.docTypes && q.docTypes.length) { params = params.set('docTypes', q.docTypes.join(',')); }
    return this.http.get<UnlistedDocsResponse>(this._getApiUrl('/unlisted'), this._options(params));
  }
}
